import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { BookOpen, X, Volume2, VolumeX, Loader2 } from "lucide-react";

export function WordOfDayModal() {
  const { user } = useAuth();
  const [wod, setWod] = useState(null);
  const [open, setOpen] = useState(false);
  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing
  const [muted, setMuted] = useState(() => localStorage.getItem("ihimm_wod_muted") === "1");
  const audioRef = useRef(null);
  const autoTried = useRef(false);

  useEffect(() => {
    if (!user) return;
    api.get("/word-of-day").then((r) => {
      const d = r.data;
      if (!d?.enabled) return;
      const stamp = `${d.updated_at || ""}|${d.message || ""}`;
      const key = "ihimm_wod_seen";
      if (sessionStorage.getItem(key) === stamp) return;
      sessionStorage.setItem(key, stamp);
      setWod(d);
      setOpen(true);
    }).catch(() => {});
  }, [user]);

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setAudioState("idle");
  };

  const listen = async () => {
    if (audioState === "playing") { stopAudio(); return; }
    setAudioState("loading");
    try {
      const res = await api.get("/word-of-day/audio", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { setAudioState("idle"); URL.revokeObjectURL(url); };
      audio.onerror = () => setAudioState("idle");
      await audio.play();
      setAudioState("playing");
    } catch { setAudioState("idle"); }
  };

  // Auto-narrate when the popup opens, unless muted (browser may block without a gesture — falls back to Listen button)
  useEffect(() => {
    if (open && wod && !muted && !autoTried.current) {
      autoTried.current = true;
      listen();
    }
  }, [open, wod, muted]);

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    localStorage.setItem("ihimm_wod_muted", next ? "1" : "0");
    if (next) stopAudio();
  };

  const close = () => { stopAudio(); setOpen(false); };

  return (
    <AnimatePresence>
      {open && wod && (
        <motion.div className="fixed inset-0 z-[200] flex items-center justify-center p-6" data-testid="word-of-day-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.6 }}>
          <div className="absolute inset-0 bg-charcoal/75 backdrop-blur-sm" onClick={close} />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-bone w-full max-w-lg border border-gold/60 shadow-2xl overflow-hidden">
            <div className="bg-navy px-8 py-5 flex items-center gap-3">
              <BookOpen size={20} className="text-gold" strokeWidth={1.6} />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-bone">{wod.heading || "Word of the Day"}</span>
            </div>
            <div className="absolute top-4 right-4 flex items-center gap-3">
              <button onClick={toggleMute} data-testid="word-of-day-mute" title={muted ? "Unmute auto-play" : "Mute auto-play"}
                className="text-bone/70 hover:text-gold transition-colors">{muted ? <VolumeX size={19} /> : <Volume2 size={19} />}</button>
              <button onClick={close} data-testid="word-of-day-close" className="text-bone/70 hover:text-gold transition-colors"><X size={20} /></button>
            </div>
            <div className="p-10">
              {wod.scripture && (
                <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                  className="font-mono text-xs uppercase tracking-[0.2em] text-gold">{wod.scripture}</motion.p>
              )}
              {wod.message && (
                <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.42 }}
                  className="font-serif text-2xl sm:text-3xl italic leading-snug tracking-tight text-charcoal mt-4">
                  {wod.message}
                </motion.p>
              )}
              <div className="mt-9 flex items-center gap-3">
                <button onClick={close} data-testid="word-of-day-amen"
                  className="bg-charcoal text-bone px-10 py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-navy transition-colors">
                  Amen
                </button>
                <button onClick={listen} data-testid="word-of-day-listen"
                  className="inline-flex items-center gap-2 border border-charcoal/30 text-charcoal px-6 py-3.5 font-mono text-xs uppercase tracking-widest hover:bg-navy hover:text-bone hover:border-navy transition-colors">
                  {audioState === "loading" ? <Loader2 size={15} className="animate-spin" /> : <Volume2 size={15} />}
                  {audioState === "playing" ? "Stop" : audioState === "loading" ? "Loading" : "Listen"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
